/* import __COLOCATED_TEMPLATE__ from './user.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type PiiService from 'embercom/services/pii-service';

interface UserData {
  id?: number;
  user: {
    id?: string;
    name?: string;
    phone?: string;
    pseudonym?: string;
    email?: string;
  };
  email_masked?: boolean;
  phone_masked?: boolean;
}

interface Args {
  data: UserData;
}

interface Signature {
  Args: Args;
}

export default class SwitchMonitorUser extends Component<Signature> {
  @service declare piiService: PiiService;

  @tracked revealedPhone: string | null = null;
  @tracked revealedEmail: string | null = null;

  get user() {
    return this.args.data?.user;
  }

  get userId(): string | undefined {
    return this.user?.id;
  }

  get isPhoneMasked(): boolean {
    return this.args.data?.phone_masked ?? false;
  }

  get isEmailMasked(): boolean {
    return this.args.data?.email_masked ?? false;
  }

  get canReveal(): boolean {
    return this.piiService.canRevealMaskedData;
  }

  /**
   * The display value follows the same fallback as the original template-only component:
   * name -> phone -> pseudonym -> email -> null
   *
   * When the displayed field is masked and has been revealed, use the revealed value.
   */
  get displayValue(): string | undefined {
    if (this.user?.name) {
      return this.user.name;
    }

    if (this.user?.phone) {
      return this.revealedPhone ?? this.user.phone;
    }

    if (this.user?.pseudonym) {
      return this.user.pseudonym;
    }

    if (this.user?.email) {
      return this.revealedEmail ?? this.user.email;
    }

    return undefined;
  }

  /**
   * The displayed field is masked if:
   * - The fallback resolved to phone and phone_masked is true (and not yet revealed)
   * - The fallback resolved to email and email_masked is true (and not yet revealed)
   */
  get displayedFieldIsMasked(): boolean {
    if (this.user?.name) {
      return false;
    }
    if (this.user?.phone) {
      return this.isPhoneMasked && this.revealedPhone === null;
    }
    if (this.user?.pseudonym) {
      return false;
    }
    if (this.user?.email) {
      return this.isEmailMasked && this.revealedEmail === null;
    }
    return false;
  }

  get revealField(): 'email' | 'phone' | null {
    if (this.user?.name) {
      return null;
    }
    if (this.user?.phone) {
      return 'phone';
    }
    if (this.user?.pseudonym) {
      return null;
    }
    if (this.user?.email) {
      return 'email';
    }
    return null;
  }

  get sourceType(): 'phone_call_redirect' | undefined {
    if (!this.userId && this.displayedFieldIsMasked && this.args.data?.id) {
      return 'phone_call_redirect';
    }
    return undefined;
  }

  get sourceId(): string | undefined {
    if (this.sourceType && this.args.data?.id) {
      return String(this.args.data.id);
    }
    return undefined;
  }

  @action
  onPiiRevealed(value: string) {
    if (this.revealField === 'phone') {
      this.revealedPhone = value;
    } else if (this.revealField === 'email') {
      this.revealedEmail = value;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::Switch::Monitor::User': typeof SwitchMonitorUser;
    'settings/switch/monitor/user': typeof SwitchMonitorUser;
  }
}
